// Single source of truth for page → permission key mapping.
// Used by App.jsx (renderPage guard) and useStore.js (lastPage validation).
// Sidebar.jsx NAV perm fields must match these values.
export const PAGE_PERMS = {
  dashboard:  'dashboard.view',
  students:   'students.view',
  families:   'families.view',
  attendance: 'attendance.view',
  complaints: 'complaints.view',
  affairs:    'behavior.view',
  fees:       'fees.view',
  pos:        'pos.view',
  inventory:  'inventory.view',
  expenses:   'expenses.view',
  finance:    'expenses.view',
  exams:      'exams.view',
  timetable:  'timetable.view',
  diary:      'diary.view',
  staff:      'staff.view',
  reports:    'reports.view',
  documents:  'documents.view',
  settings:   'settings.view',
};

// Optional modules a school can be sold. Dashboard, students, families and settings are always on.
// A permission key belongs to every module whose prefix it starts with; it is usable only when all
// of those modules are enabled (reports.fees.* needs both Reports and Fees).
export const SCHOOL_MODULES = [
  { id: 'attendance', label: 'Attendance',          prefixes: ['attendance.', 'reports.attendance.'] },
  { id: 'fees',       label: 'Fees and challans',   prefixes: ['fees.', 'reports.fees.'] },
  { id: 'finance',    label: 'Finance and expenses', prefixes: ['expenses.', 'finance.'] },
  { id: 'inventory',  label: 'Inventory',           prefixes: ['inventory.'] },
  { id: 'pos',        label: 'POS',                 prefixes: ['pos.'] },
  { id: 'exams',      label: 'Exams and results',   prefixes: ['exams.', 'reports.exams.'] },
  { id: 'papermaker', label: 'Paper Maker',         prefixes: ['exams.papermaker.'] },
  { id: 'timetable',  label: 'Timetable',           prefixes: ['timetable.'] },
  { id: 'diary',      label: 'Diary and homework',  prefixes: ['diary.'] },
  { id: 'staff',      label: 'Staff and payroll',   prefixes: ['staff.'] },
  { id: 'affairs',    label: 'Student affairs',     prefixes: ['behavior.'] },
  { id: 'complaints', label: 'Complaints',          prefixes: ['complaints.'] },
  { id: 'reports',    label: 'Reports',             prefixes: ['reports.'] },
  { id: 'documents',  label: 'Documents',           prefixes: ['documents.'] },
];

export const PLANS = {
  standard: { label: 'Standard', modules: ['attendance', 'fees', 'finance', 'exams', 'timetable', 'diary', 'staff', 'complaints', 'reports', 'documents'] },
  pro:      { label: 'Pro',      modules: SCHOOL_MODULES.map(m => m.id) },
};

// null/undefined enabledModules means every module (schools onboarded before modules existed).
export function keyAllowedByModules(enabledModules, key) {
  if (!Array.isArray(enabledModules)) return true;
  return SCHOOL_MODULES.every(m => !m.prefixes.some(p => key.startsWith(p)) || enabledModules.includes(m.id));
}

export const ALL_PERMS = [
  // System
  { key: 'users_roles.edit',          label: 'Manage Users & Roles', hardcoded: true },
  { key: 'approvals.edit',            label: 'Manage Approvals' },
  { key: 'settings.view',             label: 'View Settings' },
  { key: 'settings.edit',             label: 'Edit Settings' },
  { key: 'settings.data_reset',       label: 'Data Reset' },
  { key: 'settings.lists',            label: 'Rename List Values' },
  { key: 'account.view',              label: 'View Account' },
  { key: 'dashboard.view',            label: 'View Dashboard' },

  // Students & Families
  { key: 'families.view',             label: 'View Families' },
  { key: 'students.view',             label: 'View Students' },
  { key: 'students.add',              label: 'Add Students' },
  { key: 'students.edit',             label: 'Edit Students' },
  { key: 'students.delete',           label: 'Delete Students' },
  { key: 'students.documents.view',   label: 'View Student Documents' },
  { key: 'students.documents.upload', label: 'Upload Student Documents' },
  { key: 'students.documents.delete', label: 'Delete Student Documents' },
  { key: 'students.bulk_update',      label: 'Bulk Update Students' },
  { key: 'students.promotion',        label: 'Promote Students' },

  // Attendance
  { key: 'attendance.view',           label: 'View Attendance' },
  { key: 'attendance.edit',           label: 'Edit Attendance' },
  { key: 'attendance.edit_past',      label: 'Edit Past Dates' },
  { key: 'attendance.edit_all',       label: 'Mark attendance (all classes)' },

  // Complaints
  { key: 'complaints.view',           label: 'View Complaints' },
  { key: 'complaints.resolve',        label: 'Resolve Complaints' },
  { key: 'complaints.delete',         label: 'Delete Complaints' },

  // Student affairs & behaviour
  { key: 'behavior.view',             label: 'View Student Affairs' },
  { key: 'behavior.rate',             label: 'Rate students and log incidents' },
  { key: 'behavior.review',           label: 'Review behaviour assessments' },

  // Fees
  { key: 'fees.view',                 label: 'View Fees' },
  { key: 'fees.totals.view',          label: 'View Fee Totals' },
  { key: 'fees.assign',               label: 'Assign Fees' },
  { key: 'fees.arrear.edit',          label: 'Edit Arrears' },
  { key: 'fees.challan.generate',     label: 'Generate Challans' },
  { key: 'fees.challan.collect',      label: 'Collect Payments' },
  { key: 'fees.payment.reverse',      label: 'Reverse Payments' },
  { key: 'fees.void',                 label: 'Void Challans' },
  { key: 'fees.waiver',               label: 'Waiver / Discount' },
  { key: 'fees.advance.deposit',      label: 'Deposit Advance' },
  { key: 'fees.advance.draw',         label: 'Draw Advance' },

  // POS
  { key: 'pos.view',                  label: 'View POS' },
  { key: 'pos.sell',                  label: 'POS Sell' },
  { key: 'pos.products.edit',         label: 'Edit Products' },
  { key: 'pos.sale.reverse',          label: 'Return or Reverse POS Sales' },

  // Inventory
  { key: 'inventory.view',            label: 'View Inventory' },
  { key: 'inventory.purchase',        label: 'Receive Purchases (GRN)' },
  { key: 'inventory.adjust',          label: 'Adjust Purchases' },
  { key: 'inventory.discount',        label: 'Give Vendor Discounts (PO / GRN)' },
  { key: 'inventory.grn.edit',        label: 'Edit Received GRN' },

  // Expenses and finance
  { key: 'expenses.view',             label: 'View Expenses' },
  { key: 'expenses.voucher.create',   label: 'Create Expense Vouchers' },
  { key: 'expenses.voucher.approve',  label: 'Approve Expense Vouchers' },
  { key: 'expenses.entry.pay',        label: 'Pay Expenses' },
  { key: 'expenses.entry.edit',       label: 'Edit Expense Entries' },
  { key: 'expenses.entry.delete',     label: 'Delete Expense Entries' },
  { key: 'expenses.entry.reverse',    label: 'Reverse Expense Payments' },
  { key: 'expenses.budget.manage',    label: 'Manage Budgets' },
  { key: 'expenses.category.manage',  label: 'Manage Expense Categories' },
  { key: 'finance.movement.manage',   label: 'Record Money Movements' },
  { key: 'finance.opening_balance',   label: 'Post Opening Balances' },

  // Exams
  { key: 'exams.view',                label: 'View Exams' },
  { key: 'exams.edit',                label: 'Edit Exams' },
  { key: 'exams.schedule.view',       label: 'Exams: Schedule tab' },
  { key: 'exams.markentry.view',      label: 'Exams: Mark Entry tab' },
  { key: 'exams.results.view',        label: 'Exams: Results tab' },
  { key: 'exams.papermaker.view',     label: 'Exams: Paper Maker tab' },
  { key: 'exams.datesheet.view',      label: 'Exams: Date Sheet tab' },
  { key: 'exams.marks.confirm',       label: 'Confirm Exam Marks (review vs papers)' },

  // Timetable / Staff
  { key: 'timetable.view',            label: 'View Timetable' },
  { key: 'staff.view',                label: 'View Staff' },
  { key: 'staff.payroll.view',        label: 'View Payroll, Advances & Salary Slips' },
  { key: 'staff.payroll',             label: 'Post Payroll to Accounts' },

  // Diary
  { key: 'diary.view',                label: 'View diary' },
  { key: 'diary.write',               label: 'Write diary (assigned subjects)' },
  { key: 'diary.write_all',           label: 'Write diary (all classes)' },

  // Reports
  { key: 'reports.view',              label: 'View Reports' },
  { key: 'reports.fees.view',         label: 'Fee Reports' },
  { key: 'reports.attendance.view',   label: 'Attendance Reports' },
  { key: 'reports.exams.view',        label: 'Exam Reports' },
  { key: 'reports.academic.view',     label: 'Academic Reports' },
  { key: 'reports.student.view',      label: 'Student Reports' },

  // Documents
  { key: 'documents.view',            label: 'View Documents' },

  // Classes
  { key: 'classes.edit',              label: 'Edit Classes' },
];

export const PERMISSION_GROUPS = [
  { id: 'system',     label: 'System',              keys: ['users_roles.edit','approvals.edit','settings.view','settings.edit','settings.data_reset','settings.lists','account.view','dashboard.view'] },
  { id: 'students',   label: 'Students & Families', keys: ['families.view','students.view','students.add','students.edit','students.delete','students.documents.view','students.documents.upload','students.documents.delete','students.bulk_update','students.promotion'] },
  { id: 'attendance', label: 'Attendance',          keys: ['attendance.view','attendance.edit','attendance.edit_past','attendance.edit_all'] },
  { id: 'complaints', label: 'Complaints',          keys: ['complaints.view','complaints.resolve','complaints.delete'] },
  { id: 'behaviour',  label: 'Student Affairs', keys: ['behavior.view','behavior.rate','behavior.review'] },
  { id: 'fees',       label: 'Fees',                keys: ['fees.view','fees.totals.view','fees.assign','fees.arrear.edit','fees.challan.generate','fees.challan.collect','fees.payment.reverse','fees.void','fees.waiver','fees.advance.deposit','fees.advance.draw'] },
  { id: 'pos',        label: 'POS',                 keys: ['pos.view','pos.sell','pos.products.edit','pos.sale.reverse'] },
  { id: 'inventory',  label: 'Inventory',           keys: ['inventory.view','inventory.purchase','inventory.adjust','inventory.discount','inventory.grn.edit'] },
  { id: 'expenses',   label: 'Expenses & Finance',  keys: ['expenses.view','expenses.voucher.create','expenses.voucher.approve','expenses.entry.pay','expenses.entry.edit','expenses.entry.delete','expenses.entry.reverse','expenses.budget.manage','expenses.category.manage','finance.movement.manage','finance.opening_balance'] },
  { id: 'exams',      label: 'Exams',               keys: ['exams.view','exams.edit','exams.schedule.view','exams.markentry.view','exams.results.view','exams.papermaker.view','exams.datesheet.view','exams.marks.confirm'] },
  { id: 'academic',   label: 'Timetable & Staff',   keys: ['timetable.view','staff.view','staff.payroll.view','staff.payroll'] },
  { id: 'diary',      label: 'Diary',               keys: ['diary.view','diary.write','diary.write_all'] },
  { id: 'reports',    label: 'Reports',             keys: ['reports.view','reports.fees.view','reports.attendance.view','reports.exams.view','reports.academic.view','reports.student.view'] },
  { id: 'documents',  label: 'Documents',           keys: ['documents.view'] },
  { id: 'classes',    label: 'Classes',             keys: ['classes.edit'] },
];
